import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line max-len
import { type FinderApplicationPrivateModuleSpecsFragment } from '../../../../api/fragments/FinderApplicationPrivateModuleSpecs';
// eslint-disable-next-line max-len
import { type FinderApplicationPublicModuleSpecsFragment } from '../../../../api/fragments/FinderApplicationPublicModuleSpecs';
// eslint-disable-next-line max-len
import { UpdateFinderApplicationPrivateModuleEmailContentsDocument } from '../../../../api/mutations/updateFinderApplicationPrivateModuleEmailContents';
// eslint-disable-next-line max-len
import { UpdateFinderApplicationPublicModuleEmailContentsDocument } from '../../../../api/mutations/updateFinderApplicationPublicModuleEmailContents';
import { EmailContentUpdateType } from '../../../../api/types';
import Form from '../../../../components/fields/Form';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';
import FinderApplicationModuleEmailForm from './FinderApplicationModuleEmailForm';
import { type FinderApplicationEmailFormValues } from './type';
import useHandleFinderApplicationModuleAssets from './useHandleFinderApplicationModuleAssets';

type FinderApplicationModuleEmailProps = {
    module: FinderApplicationPublicModuleSpecsFragment | FinderApplicationPrivateModuleSpecsFragment;
};

const validator = validators.compose(
    validators.requiredString('submitOrder.subject.defaultValue.defaultValue'),
    validators.requiredString('submitOrder.introTitle.defaultValue.defaultValue'),
    validators.requiredString('submitOrder.contentText.defaultValue.defaultValue'),
    validators.requiredBoolean('submitOrder.isSummaryVehicleVisible')
);

const FinderApplicationModuleEmail = ({ module }: FinderApplicationModuleEmailProps) => {
    const { t } = useTranslation(['finderApplicationModuleDetails', 'emails']);
    const apolloClient = useApolloClient();
    const validate = useValidator(validator);

    const handleFinderApplicationModuleAssets = useHandleFinderApplicationModuleAssets();

    const { reservationPeriod } = module;

    const initialValues: FinderApplicationEmailFormValues = useMemo((): FinderApplicationEmailFormValues => {
        if (module.emailContents) {
            return module.emailContents;
        }

        return {
            submitOrder: {
                subject: {
                    defaultValue: {
                        defaultValue: t('emails:finder.submitOrder.subject'),
                        overrides: [],
                    },
                    overrides: [],
                },
                introTitle: {
                    defaultValue: {
                        defaultValue: t('emails:finder.submitOrder.introTitle'),
                        overrides: [],
                    },
                    overrides: [],
                },
                contentText: {
                    defaultValue: {
                        defaultValue: t('emails:finder.submitOrder.contentText'),
                        overrides: [],
                    },
                    overrides: [],
                },
                isSummaryVehicleVisible: true,
            },
            reminderEmail: {
                subject: {
                    defaultValue: {
                        defaultValue: t('emails:finder.submitOrder.subject'),
                        overrides: [],
                    },
                    overrides: [],
                },
                introTitle: {
                    defaultValue: {
                        defaultValue: t('emails:finder.submitOrder.introTitle'),
                        overrides: [],
                    },
                    overrides: [],
                },
                contentText: {
                    defaultValue: {
                        defaultValue: t('emails:finder.submitOrder.contentText'),
                        overrides: [],
                    },
                    overrides: [],
                },
                sendingTime: Date(),
                active: true,
            },
        };
    }, [module.emailContents, t]);

    const onSubmit = useHandleError(
        async ({ submitOrder, reminderEmail }: FinderApplicationEmailFormValues) => {
            const { introImage: submitOrderIntroImage, ...submitOrderSetting } = submitOrder;
            const { introImage: reminderEmailIntroImage, ...reminderEmailSetting } = reminderEmail;

            message.loading({
                content: t('finderApplicationModuleDetails:messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            const updateFunction =
                module.__typename === 'FinderApplicationPrivateModule'
                    ? UpdateFinderApplicationPrivateModuleEmailContentsDocument
                    : UpdateFinderApplicationPublicModuleEmailContentsDocument;

            await apolloClient
                .mutate({
                    mutation: updateFunction,
                    variables: {
                        moduleId: module.id,
                        settings: {
                            emailContentUpdateType: EmailContentUpdateType.Module,
                            submitOrder: submitOrderSetting,
                            reminderEmail: reminderEmailSetting,
                        },
                    },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            await handleFinderApplicationModuleAssets(
                module.id,
                { submitOrder, reminderEmail },
                EmailContentUpdateType.Module,
                module.emailContents
            );

            await message.success({
                content: t('finderApplicationModuleDetails:messages.success'),
                key: 'primary',
            });
        },
        [apolloClient, handleFinderApplicationModuleAssets, module.__typename, module.emailContents, module.id, t]
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form
                    id="updateEmailForm"
                    name="updateEmailForm"
                    onSubmitCapture={handleSubmit}
                    style={{ marginTop: -16 }}
                >
                    <FinderApplicationModuleEmailForm
                        companyId={module.companyId}
                        defaultValueDisabled={false}
                        reservationPeriod={reservationPeriod}
                    />
                </Form>
            )}
        </Formik>
    );
};

export default FinderApplicationModuleEmail;
